import type * as Sentry from '@sentry/react-router'

/**
 * Check if an error message should be filtered out
 * @param message - The error message to check
 * @returns true if the error should be filtered (not sent to Sentry), false otherwise
 */
const shouldFilterErrorMessage = (message: string): boolean => {
  // Do not send 404 errors or "No route matches URL" errors
  if (/No route matches URL/i.test(message)) {
    return true
  }

  // AbortError is a normal navigation abort and should not be sent
  if (/\bAbortError\b/i.test(message)) {
    return true
  }

  // Filter out hydration errors
  if (/hydration/i.test(message)) {
    return true
  }

  // Filter out CefSharp bot errors (e.g., from Outlook SafeSearch, email filters)
  if (/Object Not Found Matching Id/i.test(message)) {
    return true
  }

  return false
}

/**
 * Check if an error should be sent to Sentry
 * @param error - The error to check
 * @returns true if the error should be sent to Sentry, false otherwise
 */
export const shouldSendToSentry = (error: unknown): boolean => {
  if (error instanceof Error) {
    const msg = error.message || error.toString()
    return !shouldFilterErrorMessage(msg)
  }

  return true
}

const filterSentryEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  // Check error message
  const msg = event.exception?.values?.[0]?.value ?? ''
  if (shouldFilterErrorMessage(msg)) {
    return null
  }

  // Filter out route 404 errors
  const status = event.tags?.['response_status']
  if (status === 404) {
    return null
  }

  return event
}

export const SENTRY_DEFAULT_OPTIONS: Sentry.NodeOptions & Sentry.BrowserOptions = {
  tracesSampleRate: 0.05, // we have low-cost plan
  profileSessionSampleRate: 0.33, // 1.0 is too heavy.
  profileLifecycle: 'trace', // v11 defaults to 'manual', which never starts without startProfiler().
  // v11 replaced sendDefaultPii with dataCollection, and an unset dataCollection now
  // collects nearly everything where an unset sendDefaultPii collected almost nothing.
  // The SDK's always-on filter only matches sensitive *names* in cookies, headers and
  // query params, so the two categories it cannot protect are named here rather than
  // left to that default.
  dataCollection: {
    // The one category no name filter reaches. Loaders and actions run the login,
    // registration and payment posts through the server, so their bodies would
    // arrive at Sentry as sent.
    httpBodies: [],
    // The auth cookie is named after the sessionKey the API hands back, so nothing
    // guarantees it matches the filter's name list. No event is worth more than the
    // account a leaked cookie hands over.
    cookies: false,
    // userInfo, httpHeaders, urlQueryParams, databaseQueryData and queues stay at the
    // v11 defaults: the name filter covers headers and query params, and userInfo is
    // what makes "users affected" mean anything.
  },
  replaysSessionSampleRate: 0.005,
  replaysOnErrorSampleRate: 1,
  skipBrowserExtensionCheck: true,
  ignoreErrors: [
    'AbortError',
    /px\.ads\.linkedin\.com/,
    /Failed to fetch.*linkedin/,
    /hydration/i,
    /Object Not Found Matching Id/i,
  ],
  allowUrls: [
    // Only allow errors from our application code
    /localhost/i,
    /auba/i,
  ],
  beforeSend(event) {
    return filterSentryEvent(event)
  },
}
